'use strict';

import hoverIntent from 'hoverIntent';
import accessibilityClick from 'accessibilityClick';
import { debounce } from 'theme';
import { bp, isDesktop, isXlViewport } from 'responsive';

/**
 * CustomHeader - Advanced header navigation with dropdown menus
 *
 * Features:
 * - Multi-level dropdown navigation
 * - Hover intent detection for smooth UX
 * - Keyboard navigation support
 * - Mobile/desktop responsive behavior
 * - Focus management for accessibility
 */
class CustomHeader extends HTMLElement {
  // Manipulated CSS classes
  #hasMenuOpenClass = '_has-menu-open';
  #noScrollClass = 'no-scroll';
  #activeMenuClass = '_has-active-menu';

  // dom selectors
  #menuSelector = '[data-menu-dropdown]';
  #openMenuSelector = `${this.#menuSelector}[aria-expanded="true"]`;
  #triggerSelector = '[data-menu-dd-btn]';
  #wrapperSelector = '[data-menu-item]';
  #menuBlockListSelector = '.menu-block__inner';
  #activeButtonsSelector = `${this.#triggerSelector}.${this.#activeMenuClass}`;
  #extMenuTriggerSelectorBtn = 'button[data-external-menu-trigger]';
  #activeLinksSelector = `.menu-block__list--item.active-link`;

  constructor() {
    super();

    // State flags
    this.hasFocus = false;
    this.isMobile = null;

    // Cache DOM elements
    this.headerPrimaryNavList = this.querySelector('.header-primary__nav--list');
    this.dropdownTriggers = this.querySelectorAll(this.#triggerSelector);
    this.externalMenuTriggers = this.querySelectorAll(this.#extMenuTriggerSelectorBtn);
    this.activeLinks = this.querySelectorAll(this.#activeLinksSelector);
    this.menuBlockList = this.querySelectorAll(this.#menuBlockListSelector);
    this.mouseCaptureExit = this.querySelector('[data-mouse-capture-exit]');
    this.mobileMenuButton = this.querySelector('[data-mobile-menu-button]');
    this.headerPrimary = this.querySelector('.header-primary');
    this.headerPrimaryNav = this.querySelector('.header-primary__nav');
    this.backButtons = this.querySelectorAll('[data-menu-back-button]');
    this.firstMenuTrigger = this.dropdownTriggers.length ? this.dropdownTriggers[0] : null;
  }

  connectedCallback() {
    this.cleanMultipleActiveLinks();
    this.calcInitialValues();
    this.eventsHandler();
  }

  cleanMultipleActiveLinks() {
    if (this.activeLinks.length <= 1) return;
    this.activeLinks.forEach((link, index) => {
      if (index > 0) {
        link.classList.remove('active-link');
      }
    });
  }

  /**
   * Calculate and set CSS custom properties for layout
   * Sets viewport dimensions and navigation positioning
   */
  calcInitialValues() {
    // Set navigation list dimensions for absolute positioning
    if (this.headerPrimaryNavList) {
      this.style.setProperty('--_primary-nav-list-top', `${this.headerPrimaryNavList.offsetTop}px`);
      this.style.setProperty('--_primary-nav-list-height', `${this.headerPrimaryNavList.offsetHeight}px`);
    }

    if (this.headerPrimary) {
      const headerBounding = this.headerPrimary.getBoundingClientRect();
      this.style.setProperty('--_primary-wrapper-width', `${headerBounding?.width}px`);
    }

    // Set header height for page layout calculations
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);

    // Determine viewport breakpoints
    this.isMobile = !isDesktop();

    this.externalMenuTriggers.forEach((trigger) => {
      if (!this.isMobile) {
        trigger.setAttribute('disabled', 'true');
      } else {
        trigger.removeAttribute('disabled');
      }
    });

    this.style.setProperty('--_link-width', `${this.menuBlockList[0]?.offsetWidth || 0}px`);

    // Calculate centering offset for XL viewports (1920px+)
    const xDistance = isXlViewport() ? (window.innerWidth - bp.xl) / 2 : 0;
    this.style.setProperty('--_viewport-x-distance', `${xDistance}px`);
  }

  /**
   * Set up all event listeners for navigation interactions
   */
  eventsHandler() {
    // Configure each dropdown trigger
    this.dropdownTriggers.forEach((trigger) => this.setupTriggerEvents(trigger));

    // Global navigation events
    this.setupGlobalNavEvents();

    // Window resize handling
    const resizeHandlerDebounced = debounce(() => this.resizeHandler(), 300);

    window.addEventListener('resize', resizeHandlerDebounced);

    // Mobile menu toggle
    accessibilityClick((e) => {
      e.preventDefault();
      this.mobileMenuToggle();
    }, this.mobileMenuButton);

    // Back button handlers
    this.backButtons.forEach((backButton) => {
      accessibilityClick((e) => {
        e.preventDefault();
        this.closeMenu(backButton, true);
      }, backButton);
    });
  }

  /**
   * Set up events for individual dropdown trigger
   * @param {HTMLElement} trigger - The menu trigger button/link
   */
  setupTriggerEvents(trigger) {
    const triggerWrapper = trigger.closest(this.#wrapperSelector);
    const hoverDelay = parseInt(trigger.dataset.externalMenuTrigger ? 50 : this.dataset.hoverDelay || 350);

    // Hover intent for desktop
    hoverIntent(triggerWrapper, {
      interval: hoverDelay,
      sensitivity: 6,
      onEnter: () => {
        if (this.hasFocus || this.isMobile) return;
        this.openMenu(trigger);
      },
      onLeave: () => {
        if (this.hasFocus || this.isMobile) return;
        if (!trigger.dataset.externalMenuTrigger) {
          this.closeMenu(trigger, false);
        }
      },
    });

    // Click handler for mobile
    accessibilityClick((e) => {
      if (!this.isMobile) return;
      const hasSubMenu = trigger.getAttribute('aria-haspopup') === 'true';
      if (!hasSubMenu) return;
      e.preventDefault();
      this.openMenu(trigger);
    }, trigger);

    // Focus handling for keyboard navigation
    trigger.addEventListener('focus', () => {
      if (this.isMobile) return;
      this.hasFocus = true;
      this.openMenu(trigger);
    });

    // Focusout handling to close menus when focus leaves
    triggerWrapper.addEventListener('focusout', (e) => {
      if (!this.hasFocus || this.isMobile) return;
      const relTarget = e.relatedTarget;
      if (!relTarget?.closest('.menu-block')) return;
      if (!relTarget.dataset.menuDdBtn && !triggerWrapper.contains(relTarget)) {
        this.closeSiblingsMenus(trigger);
      }
    });
  }

  /**
   * Set up global navigation event listeners
   */
  setupGlobalNavEvents() {
    // Close menus when focus leaves navigation area
    this.headerPrimaryNav.addEventListener('focusout', (e) => {
      // contains already handle null relatedTarget
      if (!this.hasFocus || this.isMobile) return;
      if (!this.headerPrimaryNav.contains(e.relatedTarget)) {
        this.closeAllMenus();
        this.hasFocus = false;
      }
    });

    // Escape key to close all menus
    this.headerPrimaryNav.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      this.closeAllMenus();
      this.hasFocus = false;
      const focusTarget = this.isMobile ? this.mobileMenuButton : document.querySelector('.skip-to-search');
      focusTarget?.focus();
    });

    // Mouse exit area to close menus
    hoverIntent(this.mouseCaptureExit, {
      interval: 50,
      sensitivity: 6,
      onEnter: () => {
        if (this.hasFocus || this.isMobile) return;
        this.closeAllMenus();
      },
    });
  }

  /**
   * Handle window resize events
   * Recalculates dimensions and closes open menus
   */
  resizeHandler() {
    this.closeAllMenus();
    this.calcInitialValues();
  }

  /**
   * Close all open menus and reset state
   */
  closeAllMenus() {
    if (!this.headerPrimaryNav?.classList.contains(this.#hasMenuOpenClass)) return;

    const openMenus = this.querySelectorAll(this.#openMenuSelector);
    const menuButtons = this.querySelectorAll(this.#activeButtonsSelector);

    // Reset aria-expanded on all open menus
    openMenus.forEach((menu) => menu.setAttribute('aria-expanded', 'false'));

    // Remove active class from all buttons
    menuButtons.forEach((btn) => btn.classList.remove(this.#activeMenuClass));

    // Reset global state
    this.headerPrimaryNav.classList.remove(this.#hasMenuOpenClass);
    this.mobileMenuButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(this.#noScrollClass);
  }

  /**
   * Close a specific menu
   * @param {HTMLElement} trigger - The trigger element or close button
   * @param {boolean} isCloseButton - Whether this is triggered by a close button
   */
  closeMenu(trigger, isCloseButton) {
    let dropdown, triggerButton;

    if (isCloseButton) {
      // Close button clicked - find parent menu and trigger
      dropdown = trigger.closest(this.#menuSelector);
      const parentItem = trigger.closest(this.#wrapperSelector);
      triggerButton = parentItem.querySelector(this.#activeButtonsSelector);
    } else {
      // Direct trigger close
      dropdown = trigger.nextElementSibling;
      triggerButton = trigger;
    }

    // Close the menu
    dropdown.setAttribute('aria-expanded', 'false');
    triggerButton.classList.remove(this.#activeMenuClass);

    // Check if we should close all or preopen another menu
    const openMenu = this.querySelector(this.#openMenuSelector);
    if (!openMenu) {
      this.headerPrimaryNav.classList.remove(this.#hasMenuOpenClass);
      document.body.classList.remove(this.#noScrollClass);
    } else if (!isCloseButton && openMenu.dataset.preopenMenu === 'true') {
      // Auto-open first submenu if configured
      const firstInnerTrigger = openMenu.querySelector(this.#triggerSelector);
      if (firstInnerTrigger) {
        this.openMenu(firstInnerTrigger);
      }
    }
  }

  /**
   * Close sibling menus at the same level
   * @param {HTMLElement} trigger - The trigger of the menu to keep open
   */
  closeSiblingsMenus(trigger) {
    const parentList = trigger.closest(this.#menuSelector);
    const expandedSiblings = parentList.querySelectorAll(this.#openMenuSelector);
    const otherTriggers = parentList.querySelectorAll(this.#activeButtonsSelector);

    // Remove active state from all sibling triggers
    otherTriggers.forEach((otherTrigger) => {
      otherTrigger.classList.remove(this.#activeMenuClass);
    });

    // Close all sibling menus
    expandedSiblings.forEach((sibling) => {
      sibling.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Open a menu
   * @param {HTMLElement} trigger - The trigger button to open
   */
  openMenu(trigger) {
    if (isDesktop() && trigger.dataset.externalMenuTrigger) {
      // scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Prevent body scroll when menu is open
    document.body.classList.add('no-scroll');

    // Close sibling menus first
    this.closeSiblingsMenus(trigger);

    // Check if trigger has a submenu
    const hasPopup = trigger.getAttribute('aria-haspopup');
    if (!hasPopup || hasPopup === 'false') return;

    // Activate trigger
    trigger.classList.add(this.#activeMenuClass);

    // Find and open the menu
    let currentMenu = trigger.nextElementSibling;
    if (!currentMenu?.hasAttribute('data-menu-dropdown')) {
      const parentList = trigger.closest(this.#menuSelector);
      currentMenu = parentList?.querySelector(`:scope ${this.#menuSelector}`);
    }
    if (!currentMenu) return;
    // Auto-open first submenu if configured
    if (currentMenu.dataset.preopenMenu === 'true') {
      const firstInnerTrigger = currentMenu.querySelector(this.#triggerSelector);
      if (firstInnerTrigger) {
        this.openMenu(firstInnerTrigger);
      }
    }

    // Open the menu
    currentMenu.setAttribute('aria-expanded', 'true');
    this.headerPrimaryNav.classList.add(this.#hasMenuOpenClass);
  }

  /**
   * Toggle mobile menu open/close
   */
  mobileMenuToggle() {
    const isMenuOpen = this.headerPrimaryNav.classList.contains(this.#hasMenuOpenClass);
    if (!this.firstMenuTrigger) return;
    if (!isMenuOpen) {
      this.openMenu(this.firstMenuTrigger);
      this.mobileMenuButton.setAttribute('aria-expanded', 'true');
      // 200ms is the delay on visibility hidden property in css transition,
      // transitionend cannot be used because the visibility property is removed
      // after the transition for a smoother animation
      setTimeout(() => this.firstMenuTrigger.focus(), 200);
    } else {
      this.closeAllMenus();
      this.mobileMenuButton.focus();
    }
  }
}

// Register the custom element
if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader);
}
